import { currentAuthToken } from "@homebound/auth-components";
import {
  BoundNumberField,
  Button,
  column,
  GridColumn,
  GridDataRow,
  GridTable,
  ModalProps,
  RowStyles,
  simpleDataRows,
  SimpleHeaderAndData,
  useModal,
  useSnackbar,
} from "@homebound/beam";
import { ObjectConfig, ObjectState, useFormState } from "@homebound/form-state";
import { useCallback, useMemo, useState } from "react";
import { jsonToCSV } from "react-papaparse";
import { CsvUploader } from "src/components/CsvUploader";
import {
  LotOverviewPageLotFragment,
  LotPartitionFragment,
  Maybe,
  SaveLotPartitionInput,
  useCreateDirectUnderwritingContractsMutation,
  useSaveLotPartitionsMutation,
} from "src/generated/graphql-types";
import { createImportCsvUrl } from "src/RouteUrls";
import { formatCentsToPrice } from "src/utils";

export type DirectUnderwritingLotPartitionsTableProps = {
  lotPartitions: LotPartitionFragment[];
  lot: LotOverviewPageLotFragment;
};

export function DirectUnderwritingLotPartitionsTable(props: DirectUnderwritingLotPartitionsTableProps) {
  const { lot, lotPartitions } = props;
  const { openModal } = useModal();

  const formConfig: ObjectConfig<FormInput> = useMemo(
    () => ({
      id: { type: "value" },
      lotPartitions: {
        type: "list",
        config: lotPartitionConfig,
      },
    }),
    [],
  );

  const formState = useFormState({
    config: formConfig,
    init: {
      input: lot,
      map: mapToForm,
    },
    autoSave: saveLotPartitions,
  });
  const [saveLotPartition] = useSaveLotPartitionsMutation();
  const [createDirectUnderwritingContracts] = useCreateDirectUnderwritingContractsMutation();

  async function saveLotPartitions(formState: ObjectState<FormInput>) {
    console.log("Saving Lot Partitions", formState.changedValue);
    const changedLotPartitions =
      formState.changedValue.lotPartitions?.filter(
        (lp) =>
          lp.directUnderwritingSoftCostsInCents !== undefined ||
          lp.directUnderwritingHardCostsInCents !== undefined ||
          lp.directUnderwritingHardCostMarkupBasisPoints !== undefined ||
          lp.underwrittenSalesPriceInCents !== undefined ||
          lp.underwrittenSalesCommissionBasisPoints !== undefined,
      ) || [];
    await Promise.all(
      changedLotPartitions.map(async (lp) =>
        saveLotPartition({
          variables: { input: { lotPartitions: [{ ...lp }] } },
        }),
      ),
    );
  }

  const canBookDisabled = !lot.canContractDirectUnderwriting.allowed;
  const contractToBlueprintTooltip = canBookDisabled
    ? lot.canContractDirectUnderwriting.disabledReasons.map((reason) => reason.message).join("; ")
    : "Contract all outstanding Direct Underwriting to Blueprint";

  const { addError, clearErrors, csvContent, errors, loading, setCsvContent, upload } = useUploadDirectUnderwritingCsv(
    lot.id,
  );

  return (
    <>
      <CsvUploader
        onError={addError}
        errors={errors}
        onDrop={(content) => {
          clearErrors();
          setCsvContent(
            jsonToCSV(
              content.map(({ data }) => data),
              { newline: "\n" }, // default is `\r\n` which breaks the parser on the backend
            ),
          );
        }}
      />
      <Button label="Import" disabled={!csvContent || loading} onClick={upload} />

      <GridTable
        id="underwritingLotPartitionsTable"
        rowStyles={rowStyles}
        columns={createPartitionColumns(formState, openModal)}
        rows={createPartitionRows(lotPartitions)}
        fallbackMessage="Partitions for this Lot will show here."
      />
      <Button
        variant="primary"
        disabled={canBookDisabled}
        tooltip={contractToBlueprintTooltip}
        onClick={async () => {
          await createDirectUnderwritingContracts({
            variables: {
              input: {
                lotId: lot.id,
              },
            },
          });
        }}
        label={"Confirm and Lock in Direct Underwriting to Blueprint"}
      />
    </>
  );
}

type Row = SimpleHeaderAndData<LotPartitionFragment>;

const rowStyles: RowStyles<Row> = {
  header: {},
  data: {},
};

function createPartitionRows(lotPartitions: LotPartitionFragment[]): GridDataRow<Row>[] {
  return simpleDataRows(lotPartitions);
}

function createPartitionColumns(
  formState: ObjectState<FormInput>,
  openModal: { (props: ModalProps): void },
): GridColumn<Row>[] {
  const idColumn = column<Row>({
    header: () => "Partition ID",
    data: ({ id }) => id,
    w: "100px",
  });

  const blueprintIdColumn = column<Row>({
    header: () => "Blueprint Project ID",
    data: ({ blueprintProjectId }) => blueprintProjectId,
    w: "100px",
  });

  const softCostColumn = column<Row>({
    header: () => "Direct Soft Costs",
    data: (row) => {
      const formStateField = formState.lotPartitions.rows.find((lp) => lp.id.value === row.id);
      return {
        value: () => row.directUnderwritingSoftCostsInCents,
        content: () => <BoundNumberField type="cents" field={formStateField!.directUnderwritingSoftCostsInCents} />,
        tooltip:
          "The total of underwritten soft costs for this property that are NOT included at the community/subdivision level",
      };
    },
    w: "200px",
  });

  const hardCostColumn = column<Row>({
    header: () => "Direct Hard Costs",
    data: (row) => {
      const formStateField = formState.lotPartitions.rows.find((lp) => lp.id.value === row.id);
      return {
        value: () => row.directUnderwritingHardCostsInCents,
        content: () => <BoundNumberField type="cents" field={formStateField!.directUnderwritingHardCostsInCents} />,
        tooltip:
          "The total of underwritten hard costs for this property that are NOT included at the community/subdivision level",
      };
    },
    w: "200px",
  });

  const hardCostMarkupColumn = column<Row>({
    header: () => "Hard Cost Markup %",
    data: (row) => {
      const formStateField = formState.lotPartitions.rows.find((lp) => lp.id.value === row.id);
      return {
        value: () => row.directUnderwritingHardCostMarkupBasisPoints,
        content: () => (
          <BoundNumberField type="basisPoints" field={formStateField!.directUnderwritingHardCostMarkupBasisPoints} />
        ),
        tooltip: "The markup percentage for hard costs on this property",
      };
    },
    w: "200px",
  });

  const hardCostMarkupTotalColumn = column<Row>({
    header: () => "Hard Cost Markup Total",
    data: ({ directUnderwritingHardCostMarkupBasisPoints, directUnderwritingHardCostsInCents }) => {
      if (!directUnderwritingHardCostMarkupBasisPoints || !directUnderwritingHardCostsInCents) {
        return undefined;
      }
      return formatCentsToPrice(
        directUnderwritingHardCostsInCents * (directUnderwritingHardCostMarkupBasisPoints / 10000),
      );
    },
    w: "100px",
  });

  const totalLotSpecificColumn = column<Row>({
    header: () => "Lot Specific Underwritten Costs Total",
    data: ({
      directUnderwritingHardCostMarkupBasisPoints,
      directUnderwritingHardCostsInCents,
      directUnderwritingSoftCostsInCents,
    }) => {
      if (
        !directUnderwritingHardCostMarkupBasisPoints ||
        !directUnderwritingHardCostsInCents ||
        !directUnderwritingSoftCostsInCents
      ) {
        return undefined;
      }
      return formatCentsToPrice(
        directUnderwritingHardCostsInCents * (directUnderwritingHardCostMarkupBasisPoints / 10000) +
          directUnderwritingHardCostsInCents +
          directUnderwritingSoftCostsInCents,
      );
    },

    w: "200px",
  });

  const underwrittenContractedColumn = column<Row>({
    header: () => "Current Contracted Underwritten Costs Total",
    data: ({ underwrittenOpcoTotalCostInCents }) => {
      return formatCentsToPrice(underwrittenOpcoTotalCostInCents || 0);
    },
    w: "300px",
  });

  const exitGrossPriceColumn = column<Row>({
    header: () => "Exit Gross Sale Price",
    data: (row) => {
      const formStateField = formState.lotPartitions.rows.find((lp) => lp.id.value === row.id);
      return {
        value: () => row.underwrittenSalesPriceInCents,
        content: () => <BoundNumberField type="cents" field={formStateField!.underwrittenSalesPriceInCents} />,
        tooltip: "The underwritten gross sales price for this property",
      };
    },
    w: "200px",
  });

  const exitCommissionRateColumn = column<Row>({
    header: () => "Exit Commission %",
    data: (row) => {
      const formStateField = formState.lotPartitions.rows.find((lp) => lp.id.value === row.id);
      return {
        value: () => row.underwrittenSalesCommissionBasisPoints,
        content: () => (
          <BoundNumberField type="basisPoints" field={formStateField!.underwrittenSalesCommissionBasisPoints} />
        ),
        tooltip: "The underwritten percentage for exit commission on this property",
      };
    },
    w: "200px",
  });

  const exitNetPriceColumn = column<Row>({
    header: () => "Exit Net Sale Price",
    data: ({ underwrittenSalesPriceInCents, underwrittenSalesCommissionBasisPoints }) => {
      if (!underwrittenSalesPriceInCents) {
        return undefined;
      }
      return formatCentsToPrice(underwrittenSalesPriceInCents * (1 - underwrittenSalesCommissionBasisPoints / 10000));
    },
    w: "200px",
  });

  return [
    idColumn,
    blueprintIdColumn,
    softCostColumn,
    hardCostColumn,
    hardCostMarkupColumn,
    hardCostMarkupTotalColumn,
    totalLotSpecificColumn,
    underwrittenContractedColumn,
    exitGrossPriceColumn,
    exitCommissionRateColumn,
    exitNetPriceColumn,
  ];
}

type FormInput = SaveLotPartitionInput & {
  lotPartitions: Maybe<SaveLotPartitionInput[]>;
};

function mapToForm(lot: LotOverviewPageLotFragment): FormInput {
  return {
    id: lot.id,
    lotPartitions: mapLotPartitionsToForm(lot.partitions),
  };
}

export function mapLotPartitionsToForm(lotPartitions: Maybe<LotPartitionFragment[]>) {
  return lotPartitions?.map((lp) => {
    return {
      id: lp.id,
      directUnderwritingSoftCostsInCents: lp.directUnderwritingSoftCostsInCents,
      directUnderwritingHardCostsInCents: lp.directUnderwritingHardCostsInCents,
      directUnderwritingHardCostMarkupBasisPoints: lp.directUnderwritingHardCostMarkupBasisPoints,
      underwrittenSalesPriceInCents: lp.underwrittenSalesPriceInCents,
      underwrittenSalesCommissionBasisPoints: lp.underwrittenSalesCommissionBasisPoints,
    };
  });
}

export const lotPartitionConfig: ObjectConfig<SaveLotPartitionInput> = {
  id: { type: "value" },
  directUnderwritingSoftCostsInCents: { type: "value" },
  directUnderwritingHardCostsInCents: { type: "value" },
  directUnderwritingHardCostMarkupBasisPoints: { type: "value" },
  underwrittenSalesPriceInCents: { type: "value" },
  underwrittenSalesCommissionBasisPoints: { type: "value" },
};

function useUploadDirectUnderwritingCsv(lotId: string) {
  const [errors, setErrors] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [csvContent, setCsvContent] = useState("");
  const { triggerNotice } = useSnackbar();

  const addError = (err: unknown) => setErrors((existing) => existing.concat(String(err)));
  const clearErrors = () => setErrors([]);

  const upload = useCallback(async () => {
    if (loading) return;
    setLoading(true);

    const response = await fetch(`${createImportCsvUrl("directUnderwritingUpload")}&lotId=${lotId}`, {
      method: "POST",
      headers: { "content-type": "text/csv", Authorization: `Bearer ${await currentAuthToken()}` },
      body: csvContent,
    });

    if (response.status !== 200) {
      // Backend may have identified errors in the CSV (Like unfilled columns or poorly-formatted numbers)
      // so pop them into the Errors array here.
      const { message } = await response.json();
      if (message) addError(message);
      setLoading(false);
      return;
    }

    triggerNotice({ message: "Direct Underwriting Information Uploaded Successfully" });
    // Just reload the page to see the new data - come back to this later and do a better job of updating the UI
    window.location.reload();
  }, [loading, lotId, csvContent, triggerNotice]);

  return { addError, clearErrors, csvContent, errors, loading, setCsvContent, upload };
}
